/**
 * @fileoverview A state machine for handling the status of a participant.
 *
 * @example
 * const stateMachine = new StateMachine(States.review, async (toState) => {
 *     return confirm(`Are you sure you want to transition to ${toState}?`)
 * })
 * stateMachine.executeTransition(States.qualified)
 */

import { States } from '../config'

type Transition = {
    from: States
    to: States
    requiresConfirmation: boolean
}

type ConfirmationCallback = (toState: States) => Promise<boolean>

/**
 * @class StateMachine
 * @description A state machine for handling the status of a participant.
 * @param {States} initialState - The initial state of the participant.
 * @param {ConfirmationCallback} confirmationCallback - The callback function for confirming the transition.
 */
export class StateMachine {
    public currentState: States
    private transitions: Transition[]
    private confirmationCallback: ConfirmationCallback

    /**
     * @constructor
     * @description Constructs a new StateMachine instance.
     * @param {States} initialState - The initial state of the participant.
     * @param {ConfirmationCallback} confirmationCallback - The callback function for confirming the transition.
     */
    constructor(initialState: States, confirmationCallback: ConfirmationCallback) {
        this.currentState = initialState
        this.confirmationCallback = confirmationCallback
        this.transitions = [
            { from: States.review, to: States.qualified, requiresConfirmation: false },
            { from: States.review, to: States.not_qualified, requiresConfirmation: false },
            { from: States.review, to: States.invited, requiresConfirmation: false },
            { from: States.review, to: States.excluded, requiresConfirmation: false },
            { from: States.review, to: States.randomised, requiresConfirmation: true },
            { from: States.qualified, to: States.invited, requiresConfirmation: false },
            { from: States.qualified, to: States.not_qualified, requiresConfirmation: true },
            { from: States.qualified, to: States.review, requiresConfirmation: true },
            { from: States.qualified, to: States.excluded, requiresConfirmation: true },
            { from: States.qualified, to: States.randomised, requiresConfirmation: true },
            { from: States.not_qualified, to: States.excluded, requiresConfirmation: false },
            { from: States.not_qualified, to: States.qualified, requiresConfirmation: true },
            { from: States.not_qualified, to: States.invited, requiresConfirmation: true },
            { from: States.not_qualified, to: States.review, requiresConfirmation: true },
            { from: States.not_qualified, to: States.randomised, requiresConfirmation: true },
            { from: States.invited, to: States.excluded, requiresConfirmation: true },
            { from: States.invited, to: States.qualified, requiresConfirmation: true },
            { from: States.invited, to: States.not_qualified, requiresConfirmation: true },
            { from: States.invited, to: States.review, requiresConfirmation: true },
            { from: States.invited, to: States.randomised, requiresConfirmation: true },
            { from: States.excluded, to: States.not_qualified, requiresConfirmation: false },
            { from: States.excluded, to: States.review, requiresConfirmation: true },
            { from: States.excluded, to: States.invited, requiresConfirmation: true },
            { from: States.excluded, to: States.qualified, requiresConfirmation: true },
            { from: States.excluded, to: States.randomised, requiresConfirmation: true },
            { from: States.randomised, to: States.not_qualified, requiresConfirmation: true },
            { from: States.randomised, to: States.qualified, requiresConfirmation: true },
            { from: States.randomised, to: States.review, requiresConfirmation: true },
            { from: States.randomised, to: States.excluded, requiresConfirmation: true },
            { from: States.randomised, to: States.invited, requiresConfirmation: true },
        ]
    }

    /**
     * @method executeTransition
     * @description Executes a transition from the current state to the target state.
     * @param {States} toState - The target state to transition to.
     * @returns {boolean} - True if the transition is successful, false otherwise.
     */
    public async executeTransition(toState: States): Promise<boolean> {
        const transition = this.transitions.find(
            (t) => t.from === this.currentState && t.to === toState
        )

        if (!transition) {
            console.error(`Invalid transition from ${this.currentState} to ${toState}.`)
            return false
        }

        if (transition.requiresConfirmation) {
            const isConfirmed = await this.confirmationCallback(toState)
            if (isConfirmed) {
                this.currentState = toState
            }
            return true
        }

        this.currentState = toState
        return true
    }
}
